<script setup lang="ts">
import { useLoanAppProcess } from '@/helpers/useLoanAppProcess';
import type { ApplicantId } from '@/types';
import { ref, watch, computed } from 'vue';

export interface ApplicantPropertyShare {
  id: ApplicantId;
  share?: number;
}

const { loanApplicants } = useLoanAppProcess();

const props = defineProps<{
  modelValue: Record<ApplicantId, number>;
  error?: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', v: Record<ApplicantId, number>): void;
  (e: 'validation', v: { isValid: boolean; total: number; shouldShowValidation: boolean }): void;
}>();

const applicants = ref<ApplicantPropertyShare[]>([]);

const totalOwnership = computed(() => {
  return applicants.value.reduce((sum, applicant) => {
    const share = applicant.share ?? 0;
    return sum + (share === -1 ? 0 : share);
  }, 0);
});

const isValidOwnership = computed(() => {
  return totalOwnership.value === 100;
});

const checkInput = (id: number) => {
  const share = applicants.value[id].share ?? 0;

  if (share < 0) {
    applicants.value[id].share = 0;
  } else if (share > 100) {
    applicants.value[id].share = 100;
  }

  const newValue = applicants.value.reduce((obj, item) => {
    obj[item.id] = item.share ?? 0;
    return obj;
  }, {} as Record<ApplicantId, number>);
  emit('update:modelValue', newValue);

  emit('validation', {
    isValid: isValidOwnership.value,
    total: totalOwnership.value,
    shouldShowValidation: true,
  });
};

const onlyNumbers = (event: KeyboardEvent) => {
  if (event.key < '0' || event.key > '9') {
    event.preventDefault();
  }
};

const applicantName = (id: ApplicantId) => {
  const applicant = loanApplicants.value[id];
  if (!applicant) return '';
  return applicant.firstName + ' ' + applicant.lastName;
};

watch(
  () => props.modelValue,
  (val) => {
    applicants.value = Object.entries(val).map(([id, share]) => ({ id, share }));
  },
  { immediate: true },
);
</script>

<template>
  <div class="max-w-500 w-full">
    <div v-for="(applicant, index) in applicants" :key="applicant.id" class="pt-6">
      <BxInput
        :model-value="applicant.share === -1 ? '' : applicant.share?.toString()"
        :label="applicants.length > 1 ? applicantName(applicant.id) + ' ownership' : ''"
        :maxlength="String(applicant.share ?? '').startsWith('1') ? '3' : '2'"
        label-type="floating"
        :has-error="error"
        @update:model-value="(value: string | undefined) => {
          const parsed = Number.parseInt(String(value ?? ''), 10);
          applicant.share = Number.isFinite(parsed) ? Math.min(Math.max(parsed, 0), 100) : undefined;
          checkInput(index);
        }"
        @keypress="onlyNumbers"
      >
        <template #trailing>
          <span class="mx-4 text-gray-400">%</span>
        </template>
      </BxInput>
    </div>
  </div>
</template>
